import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import {
  RiTable2,
  RiInsertRowBottom,
  RiInsertColumnRight,
  RiDeleteRow,
  RiDeleteColumn,
  RiDeleteBin6Line,
} from 'react-icons/ri';
import type { TranslationKey } from '@typedefs/modulesInterfaces';

// Quill binds the toolbar by selector; there is only one report modal on a page.
const TOOLBAR_ID = 'report-assumptions-toolbar';

// Quill 2's built-in table module (getModule('table')).
interface TableModule {
  insertTable: (rows: number, columns: number) => void;
  getTable: () => [unknown, unknown, unknown, number];
  insertRowBelow: () => void;
  insertColumnRight: () => void;
  deleteRow: () => void;
  deleteColumn: () => void;
  deleteTable: () => void;
}

type TableAction = 'insertRowBelow' | 'insertColumnRight' | 'deleteRow' | 'deleteColumn' | 'deleteTable';

// Shown only while the cursor is inside a table.
const TABLE_ACTIONS: { action: TableAction; icon: IconType; label: TranslationKey }[] = [
  { action: 'insertRowBelow', icon: RiInsertRowBottom, label: 'project.table_add_row' },
  { action: 'insertColumnRight', icon: RiInsertColumnRight, label: 'project.table_add_column' },
  { action: 'deleteRow', icon: RiDeleteRow, label: 'project.table_delete_row' },
  { action: 'deleteColumn', icon: RiDeleteColumn, label: 'project.table_delete_column' },
  { action: 'deleteTable', icon: RiDeleteBin6Line, label: 'project.table_delete' },
];

interface ReportAssumptionsEditorProps {
  value: string;
  // hasText is false for an editor with no text, even if it holds an empty table.
  onChange: (html: string, hasText: boolean) => void;
}

const ReportAssumptionsEditor = ({ value, onChange }: ReportAssumptionsEditorProps) => {
  const { t } = useTranslation();
  const editorRef = useRef<ReactQuill>(null);
  const [inTable, setInTable] = useState(false);
  const modules = useMemo(() => ({ toolbar: { container: `#${TOOLBAR_ID}` }, table: true }), []);

  const tableModule = () => editorRef.current?.getEditor().getModule('table') as TableModule | undefined;

  const insertTable = () => {
    editorRef.current?.getEditor().focus();
    tableModule()?.insertTable(3, 3);
    setInTable(true);
  };

  const runTableAction = (action: TableAction) => {
    tableModule()?.[action]();
    if (action === 'deleteTable') setInTable(false);
  };

  // Table buttons must not steal focus: Quill's table commands act on the current selection.
  const keepFocus = (event: React.MouseEvent) => event.preventDefault();

  return (
    <div className="report-assumptions-editor">
      <div id={TOOLBAR_ID}>
        <span className="ql-formats">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-underline" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-list" value="ordered" />
          <button type="button" className="ql-list" value="bullet" />
          <button type="button" className="ql-link" />
        </span>
        <span className="ql-formats">
          <button type="button" onMouseDown={keepFocus} onClick={insertTable} title={t('project.table_insert')} aria-label={t('project.table_insert')}>
            <RiTable2 />
          </button>
          {inTable && TABLE_ACTIONS.map(({ action, icon: Icon, label }) => (
            <button
              key={action}
              type="button"
              onMouseDown={keepFocus}
              onClick={() => runTableAction(action)}
              title={t(label)}
              aria-label={t(label)}
            >
              <Icon />
            </button>
          ))}
        </span>
      </div>
      <ReactQuill
        ref={editorRef}
        theme="snow"
        modules={modules}
        value={value}
        onChange={(html, _delta, _source, editor) => onChange(html, editor.getText().trim() !== '')}
        onChangeSelection={range => setInTable(!!range && !!tableModule()?.getTable()[0])}
        aria-label={t('project.report_assumptions_prompt')}
      />
    </div>
  );
};

export default ReportAssumptionsEditor;
